import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Package, Truck, Download, RotateCcw, MapPin, CreditCard, ExternalLink } from "lucide-react";
import { AuthContext } from "../../contexts/AuthContext";
import { ToastContext } from "../../contexts/ToastContext";
import { CartContext } from "../../contexts/CartContext";
import { apiFetch } from "../../services/api";
import { useAuthedApi } from "../../hooks/useAuthedApi";
import { formatDate, formatCurrency } from "../../utils/formatters";
import {
  resolveOrderStatus,
  resolveOrderStage,
  resolveOrderLabel,
  resolveOrderBadgeColor,
  resolveOrderItems,
  resolveOrderItemImage,
  resolveOrderTimeline,
  printOrderInvoice,
} from "../../utils/orderHelpers";
import { ORDER_STEPS } from "../../constants/orders";
import { T } from "../../constants/theme";

const DANGER = "#A94442";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "processing", label: "Processing" },
  { key: "packed", label: "Packed" },
  { key: "shipped", label: "Shipped" },
  { key: "delivered", label: "Delivered" },
  { key: "cancelled", label: "Cancelled" },
];

function matchesFilter(order, key) {
  const status = resolveOrderStatus(order);
  if (key === "all") return true;
  if (key === "processing") return ["pending", "processing", "confirmed"].includes(status);
  if (key === "packed") return status === "packed";
  if (key === "shipped") return ["shipped", "out_for_delivery"].includes(status);
  if (key === "delivered") return status === "delivered";
  if (key === "cancelled") return ["cancelled", "refunded", "returned"].includes(status);
  return true;
}

function orderSearchText(order) {
  const items = resolveOrderItems(order);
  return [
    `order #${order.id}`,
    String(order.id),
    ...items.map((i) => i.name),
    ...items.map((i) => i.brand_name),
    formatDate(order.created_at),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function ThumbStack({ items }) {
  const shown = items.slice(0, 3);
  const remaining = items.length - shown.length;
  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      {shown.map((item, i) => (
        <img
          key={item.id || i}
          src={resolveOrderItemImage(item)}
          alt={item.name}
          style={{
            width: 56,
            height: 56,
            borderRadius: 12,
            objectFit: "cover",
            border: `1px solid ${T.border}`,
            background: T.surface,
            marginLeft: i === 0 ? 0 : -14,
            boxShadow: "0 2px 6px rgba(0,0,0,.04)",
          }}
        />
      ))}
      {remaining > 0 && (
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 12,
            marginLeft: -14,
            background: T.ink,
            color: T.gold,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 800,
            border: `1px solid ${T.border}`,
          }}
        >
          +{remaining}
        </div>
      )}
    </div>
  );
}

function MiniProgress({ stageIndex, cancelled }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 6, marginTop: 16 }}>
      {ORDER_STEPS.map((step, index) => {
        const active = !cancelled && stageIndex >= index;
        return (
          <div
            key={step}
            style={{
              height: 6,
              borderRadius: 999,
              background: active ? (step === "delivered" ? T.ink : T.gold) : T.border,
              transition: "background .3s ease",
            }}
          />
        );
      })}
    </div>
  );
}

function OrderCard({ order, isActive, onSelect }) {
  const items = resolveOrderItems(order);
  const status = resolveOrderStatus(order);
  const cancelled = ["cancelled", "refunded", "returned"].includes(status);
  const stageIndex = resolveOrderStage(order);
  const primaryName = items[0]?.name || `Order #${order.id}`;
  const extraCount = items.length > 1 ? items.length - 1 : 0;

  return (
    <motion.button
      layout
      onClick={() => onSelect(order.id)}
      whileHover={{ y: -2 }}
      style={{
        textAlign: "left",
        width: "100%",
        background: isActive ? T.goldLight : T.white,
        border: isActive ? `1.5px solid ${T.gold}` : `1px solid ${T.border}`,
        borderRadius: 20,
        padding: 20,
        cursor: "pointer",
        boxShadow: isActive ? "0 12px 30px rgba(196,154,60,.18)" : "0 6px 18px rgba(17,17,16,.04)",
        transition: "box-shadow .3s ease, border-color .3s ease",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <ThumbStack items={items} />
        <div style={{ textAlign: "right" }}>
          <span
            style={{
              display: "inline-block",
              background: resolveOrderBadgeColor(order) + "15",
              color: resolveOrderBadgeColor(order),
              border: `1px solid ${resolveOrderBadgeColor(order)}30`,
              borderRadius: 999,
              padding: "5px 12px",
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: ".06em",
              textTransform: "uppercase",
            }}
          >
            {resolveOrderLabel(order)}
          </span>
          <div style={{ marginTop: 10, fontWeight: 800, fontSize: 16, color: T.ink }}>
            {formatCurrency(order.total)}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.ink, lineHeight: 1.3 }}>
          {primaryName}
          {extraCount > 0 ? ` +${extraCount} More` : ""}
        </div>
        <div style={{ marginTop: 6, color: T.muted, fontSize: 13 }}>
          {items.length} {items.length === 1 ? "Item" : "Items"} &middot; Order #{order.id}
        </div>
        <div style={{ color: T.muted, fontSize: 13 }}>Placed {formatDate(order.created_at)}</div>
      </div>

      <MiniProgress stageIndex={stageIndex} cancelled={cancelled} />
    </motion.button>
  );
}

function SectionCard({ children, style }) {
  return (
    <div
      style={{
        background: T.surface,
        borderRadius: 16,
        padding: 18,
        border: `1px solid ${T.border}`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function SectionLabel({ children, icon: Icon }) {
  return (
    <div
      style={{
        fontSize: 12,
        color: T.muted,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: ".08em",
        marginBottom: 12,
        display: "flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      {Icon ? <Icon size={13} /> : null}
      {children}
    </div>
  );
}

function OrderDetailPanel({ order, detailLoading, onReorder }) {
  if (!order) {
    return (
      <div style={{ textAlign: "center", padding: 40, color: T.muted }}>
        Select an order to view its details.
      </div>
    );
  }

  const items = resolveOrderItems(order);
  const timeline = resolveOrderTimeline(order);
  const hasTracking = Boolean(order.courier_name || order.tracking_number);
  const hasAddress = Boolean(order.shipping_name || order.shipping_line1);

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={order.id}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 20 }}>
          <div>
            <p style={{ margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: T.gold }}>
              Investment Details
            </p>
            <h2 style={{ fontFamily: "'Georgia', serif", fontSize: 26, color: T.ink, margin: "6px 0 0" }}>
              Investment #{order.id}
            </h2>
          </div>
          <span
            style={{
              background: resolveOrderBadgeColor(order) + "15",
              color: resolveOrderBadgeColor(order),
              border: `1px solid ${resolveOrderBadgeColor(order)}30`,
              borderRadius: 999,
              padding: "6px 14px",
              fontSize: 12,
              fontWeight: 800,
              textTransform: "uppercase",
              whiteSpace: "nowrap",
            }}
          >
            {resolveOrderLabel(order)}
          </span>
        </div>

        <div style={{ display: "flex", gap: 10, marginBottom: 22, flexWrap: "wrap" }}>
          <button
            onClick={() => printOrderInvoice(order)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: T.ink,
              color: T.gold,
              border: "none",
              borderRadius: 12,
              padding: "11px 18px",
              fontWeight: 700,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            <Download size={15} /> Download Invoice
          </button>
          <button
            onClick={() => onReorder(items)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: T.white,
              color: T.ink,
              border: `1px solid ${T.gold}`,
              borderRadius: 12,
              padding: "11px 18px",
              fontWeight: 700,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            <RotateCcw size={15} /> Reorder
          </button>
        </div>

        <div style={{ marginBottom: 22 }}>
          <SectionLabel>Invested In</SectionLabel>
          {detailLoading && !items.length ? (
            <div style={{ color: T.muted }}>Loading items…</div>
          ) : items.length ? (
            <div style={{ display: "grid", gap: 12 }}>
              {items.map((item) => (
                <div
                  key={item.id || `${item.product_id}-${item.name}`}
                  style={{ display: "flex", gap: 12, borderBottom: `1px solid ${T.border}`, paddingBottom: 12 }}
                >
                  <img
                    src={resolveOrderItemImage(item)}
                    alt={item.name}
                    style={{ width: 64, height: 64, borderRadius: 12, objectFit: "cover", border: `1px solid ${T.border}`, background: T.surface }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: T.ink, fontWeight: 700, fontSize: 15 }}>{item.name}</div>
                    {item.brand_name && (
                      <div style={{ color: T.muted, fontSize: 12, textTransform: "uppercase", letterSpacing: ".06em", marginTop: 2 }}>
                        {item.brand_name}
                      </div>
                    )}
                    <div style={{ color: T.muted, fontSize: 13, marginTop: 4 }}>
                      Qty {item.qty} &middot; {formatCurrency(item.price)} each
                    </div>
                  </div>
                  <div style={{ color: T.ink, fontWeight: 700, whiteSpace: "nowrap" }}>
                    {formatCurrency(item.subtotal || item.price * item.qty)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: T.muted }}>No item details available.</div>
          )}
        </div>

        {hasAddress && (
          <div style={{ marginBottom: 16 }}>
            <SectionLabel icon={MapPin}>Shipping Address</SectionLabel>
            <SectionCard>
              <div style={{ color: T.ink, fontWeight: 700 }}>{order.shipping_name}</div>
              <div style={{ color: T.muted, fontSize: 13, lineHeight: 1.6, marginTop: 4 }}>
                {order.shipping_line1}
                {order.shipping_line2 ? <><br />{order.shipping_line2}</> : null}
                <br />
                {[order.shipping_city, order.shipping_post].filter(Boolean).join(", ")}
                <br />
                {order.shipping_country}
              </div>
            </SectionCard>
          </div>
        )}

        {order.payment_method && (
          <div style={{ marginBottom: 16 }}>
            <SectionLabel icon={CreditCard}>Payment</SectionLabel>
            <SectionCard style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ color: T.ink, fontWeight: 700, textTransform: "capitalize" }}>
                {order.payment_method.replace(/_/g, " ")}
              </div>
              {order.payment_status && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    textTransform: "uppercase",
                    letterSpacing: ".06em",
                    color: order.payment_status === "paid" ? T.gold : DANGER,
                  }}
                >
                  {order.payment_status}
                </span>
              )}
            </SectionCard>
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <SectionLabel>Order Summary</SectionLabel>
          <SectionCard>
            <div style={{ display: "grid", gap: 8, fontSize: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", color: T.muted }}>
                <span>Subtotal</span>
                <span style={{ color: T.ink }}>{formatCurrency(order.subtotal)}</span>
              </div>
              {Number(order.discount) > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", color: T.muted }}>
                  <span>Discount</span>
                  <span style={{ color: T.ink }}>-{formatCurrency(order.discount)}</span>
                </div>
              )}
              {Number(order.shipping_cost) > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", color: T.muted }}>
                  <span>Shipping</span>
                  <span style={{ color: T.ink }}>{formatCurrency(order.shipping_cost)}</span>
                </div>
              )}
              {Number(order.tax) > 0 && (
                <div style={{ display: "flex", justifyContent: "space-between", color: T.muted }}>
                  <span>Tax</span>
                  <span style={{ color: T.ink }}>{formatCurrency(order.tax)}</span>
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  borderTop: `1px solid ${T.border}`,
                  paddingTop: 8,
                  marginTop: 4,
                  fontWeight: 800,
                  fontSize: 16,
                  color: T.ink,
                }}
              >
                <span>Grand Total</span>
                <span>{formatCurrency(order.total)}</span>
              </div>
            </div>
          </SectionCard>
        </div>

        <div style={{ marginBottom: hasTracking ? 16 : 0 }}>
          <SectionLabel icon={Truck}>Shipment Timeline</SectionLabel>
          <SectionCard>
            <div style={{ display: "grid", gap: 14 }}>
              {timeline.map((step) => (
                <div key={step.key} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      background: step.done ? T.gold : T.white,
                      border: `2px solid ${step.done ? T.gold : T.border}`,
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ color: step.done ? T.ink : T.muted, fontWeight: step.done ? 700 : 500, flex: 1 }}>
                    {step.label}
                  </span>
                  <span style={{ color: T.muted, fontSize: 12 }}>
                    {step.date ? formatDate(step.date) : step.done ? "Reached" : "Pending"}
                  </span>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>

        {hasTracking && (
          <div>
            <SectionLabel icon={Package}>Tracking</SectionLabel>
            <SectionCard>
              <div style={{ display: "grid", gap: 10 }}>
                {order.courier_name && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: T.muted, fontSize: 13 }}>Courier</span>
                    <span style={{ color: T.ink, fontWeight: 700 }}>{order.courier_name}</span>
                  </div>
                )}
                {order.tracking_number && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: T.muted, fontSize: 13 }}>Tracking Number</span>
                    <span style={{ color: T.ink, fontWeight: 700 }}>{order.tracking_number}</span>
                  </div>
                )}
                {order.tracking_url && (
                  <button
                    onClick={() => window.open(order.tracking_url, "_blank", "noopener,noreferrer")}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      background: T.ink,
                      color: T.gold,
                      border: "none",
                      borderRadius: 12,
                      padding: "11px 16px",
                      fontWeight: 700,
                      fontSize: 13,
                      cursor: "pointer",
                      marginTop: 4,
                    }}
                  >
                    Track Package <ExternalLink size={14} />
                  </button>
                )}
              </div>
            </SectionCard>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}

export default function OrdersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialOrderId = (() => {
    const id = Number(searchParams.get("id"));
    return Number.isFinite(id) && id > 0 ? id : null;
  })();
  const { user, token } = useContext(AuthContext);
  const toast = useContext(ToastContext);
  const { addItem } = useContext(CartContext);
  const { data: orders, loading, error } = useAuthedApi("/orders", token, [token]);
  const [selectedOrderId, setSelectedOrderIdState] = useState(initialOrderId);
  const setSelectedOrderId = (id) => {
    setSelectedOrderIdState(id);
    setSearchParams(id ? { id: String(id) } : {}, { replace: true });
  };
  const [orderDetails, setOrderDetails] = useState({});
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!orders?.length) return;
    if (selectedOrderId && orders.some((o) => o.id === selectedOrderId)) return;
    setSelectedOrderId(orders[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, selectedOrderId]);

  useEffect(() => {
    if (!token || !selectedOrderId) return;
    let mounted = true;
    setDetailLoading(true);
    apiFetch(`/orders/${selectedOrderId}`, {}, { authToken: token })
      .then(async (r) => {
        const body = await r.json().catch(() => null);
        if (!r.ok) throw new Error(body?.error || "Unable to load order details");
        return body;
      })
      .then((body) => {
        if (!mounted) return;
        setOrderDetails((prev) => ({ ...prev, [selectedOrderId]: body }));
      })
      .catch((e) => {
        if (!mounted) return;
        toast(e.message || "Unable to load order details", "error");
      })
      .finally(() => {
        if (mounted) setDetailLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [selectedOrderId, token, toast]);

  const safeOrders = useMemo(() => (Array.isArray(orders) ? orders : []), [orders]);

  const filteredOrders = useMemo(() => {
    const q = search.trim().toLowerCase();
    return safeOrders.filter((order) => {
      if (!matchesFilter(order, activeFilter)) return false;
      if (!q) return true;
      return orderSearchText(order).includes(q);
    });
  }, [safeOrders, activeFilter, search]);

  const activeOrder =
    orderDetails[selectedOrderId] ||
    safeOrders.find((o) => o.id === selectedOrderId) ||
    null;

  const summary = useMemo(() => {
    const activeDeliveries = safeOrders.filter((o) =>
      ["pending", "processing", "confirmed", "packed", "shipped", "out_for_delivery"].includes(
        resolveOrderStatus(o),
      ),
    ).length;
    const lifetimeSpend = safeOrders.reduce((sum, o) => sum + Number(o.total || 0), 0);
    return { count: safeOrders.length, activeDeliveries, lifetimeSpend };
  }, [safeOrders]);

  const handleReorder = (items) => {
    if (!items.length) return;
    items.forEach((item) => {
      if (item.product_id) addItem(item.product_id, item.qty);
    });
  };

  if (!user) {
    return (
      <div style={{ maxWidth: 760, margin: "60px auto", padding: "0 24px" }}>
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 20, padding: 40, textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📦</div>
          <h1 style={{ fontFamily: "'Georgia', serif", fontSize: 30, color: T.ink, margin: "0 0 10px" }}>My Investments</h1>
          <p style={{ color: T.muted, margin: "0 0 24px", lineHeight: 1.6 }}>
            Sign in to view your investment history, project funding status, and returns.
          </p>
          <button
            onClick={() => navigate("/login")}
            style={{ background: T.ink, color: T.gold, border: "none", borderRadius: 12, padding: "12px 24px", fontWeight: 700, cursor: "pointer" }}
          >
            Sign in to continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1240, margin: "0 auto", padding: "36px 24px 70px" }}>
      <style>{`
        .orders-grid { display: grid; grid-template-columns: 1.15fr .85fr; gap: 24px; align-items: start; }
        .orders-filter-pill { transition: background .25s ease, color .25s ease, transform .2s ease; }
        .orders-filter-pill:hover { transform: translateY(-1px); }
        @media (max-width: 900px) {
          .orders-grid { grid-template-columns: 1fr; }
          .orders-detail-sticky { position: static !important; }
        }
      `}</style>

      <div style={{ textAlign: "center", maxWidth: 640, margin: "0 auto 28px" }}>
        <div style={{ color: T.gold, textTransform: "uppercase", letterSpacing: ".2em", fontSize: 12, fontWeight: 700, marginBottom: 10 }}>
          Your Portfolio
        </div>
        <h1 style={{ margin: 0, fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(30px,4vw,42px)", fontWeight: 600, color: T.ink, lineHeight: 1.1 }}>
          My Investments
        </h1>
        <div style={{ width: 60, height: 2, background: T.gold, margin: "14px auto" }} />
        <p style={{ margin: 0, color: T.muted, fontSize: 15, lineHeight: 1.7 }}>
          Track your contributions, download confirmations and view investment history.
        </p>
        <button
          onClick={() => navigate("/shop")}
          style={{
            marginTop: 16,
            border: `1px solid ${T.gold}`,
            background: T.white,
            color: T.ink,
            padding: "11px 24px",
            borderRadius: 999,
            fontWeight: 700,
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          Browse Projects
        </button>
      </div>

      {safeOrders.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 24,
            justifyContent: "center",
            background: T.white,
            border: `1px solid ${T.border}`,
            borderRadius: 16,
            padding: "16px 24px",
            marginBottom: 24,
            boxShadow: "0 8px 20px rgba(0,0,0,.03)",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: T.ink }}>{summary.count}</div>
            <div style={{ fontSize: 12, color: T.muted, textTransform: "uppercase", letterSpacing: ".06em" }}>
              {summary.count === 1 ? "Investment" : "Investments"}
            </div>
          </div>
          <div style={{ width: 1, background: T.border }} />
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: T.ink }}>{summary.activeDeliveries}</div>
            <div style={{ fontSize: 12, color: T.muted, textTransform: "uppercase", letterSpacing: ".06em" }}>Active Projects</div>
          </div>
          <div style={{ width: 1, background: T.border }} />
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: T.ink }}>{formatCurrency(summary.lifetimeSpend)}</div>
            <div style={{ fontSize: 12, color: T.muted, textTransform: "uppercase", letterSpacing: ".06em" }}>Total Invested</div>
          </div>
        </div>
      )}

      <div style={{ position: "relative", marginBottom: 18 }}>
        <Search size={16} style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: T.muted }} />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by order number, product name, brand or date"
          style={{
            width: "100%",
            padding: "13px 16px 13px 42px",
            borderRadius: 12,
            border: `1px solid ${T.border}`,
            background: T.white,
            fontSize: 14,
            outline: "none",
          }}
        />
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 24 }}>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            className="orders-filter-pill"
            onClick={() => setActiveFilter(f.key)}
            style={{
              background: activeFilter === f.key ? T.ink : T.surface,
              color: activeFilter === f.key ? T.gold : T.ink,
              border: `1px solid ${T.border}`,
              borderRadius: 999,
              padding: "10px 20px",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 20, padding: "70px 40px", textAlign: "center", color: T.muted, boxShadow: "0 10px 30px rgba(0,0,0,.05)" }}>
          Loading your orders…
        </div>
      ) : error ? (
        <div style={{ background: T.white, border: `1px solid ${T.border}`, color: DANGER, borderRadius: 20, padding: 24 }}>
          {error.message}
        </div>
      ) : safeOrders.length === 0 ? (
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 20, padding: 60, textAlign: "center" }}>
          <div
            style={{
              width: 110,
              height: 110,
              borderRadius: "50%",
              background: T.surface,
              border: `1px solid ${T.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 50,
              margin: "0 auto 30px",
            }}
          >
            📦
          </div>
          <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", color: T.ink, margin: "0 0 8px", fontSize: 26 }}>No Investments Yet</h2>
          <p style={{ color: T.muted, margin: "0 0 24px" }}>Discover farm and crop projects and make your first investment.</p>
          <button
            onClick={() => navigate("/shop")}
            style={{
              background: `linear-gradient(135deg, ${T.gold} 0%, ${T.goldHover} 100%)`,
              color: T.white,
              boxShadow: T.shadowSoft,
              border: "none",
              borderRadius: 12,
              padding: "13px 26px",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Browse Projects
          </button>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 20, padding: 50, textAlign: "center", color: T.muted }}>
          No orders match your search or filter.
        </div>
      ) : (
        <div className="orders-grid">
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {filteredOrders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                isActive={selectedOrderId === order.id}
                onSelect={setSelectedOrderId}
              />
            ))}
          </div>

          <div className="orders-detail-sticky" style={{ position: "sticky", top: 90 }}>
            <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 20, padding: 24, boxShadow: "0 10px 28px rgba(17,17,16,.06)" }}>
              <OrderDetailPanel order={activeOrder} detailLoading={detailLoading} onReorder={handleReorder} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
